<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { brand, texts as brandTexts, primaryColor } from '@/config/brands'

const emit = defineEmits<{
  (e: 'open-drawer'): void
}>()

const isStorlyn = brand === 'storlyn' || brand === 'novaris'

const router = useRouter()
const route = useRoute()

// 在以下页面不显示全局头部
const hiddenRoutes = ['home', 'chapter', 'chapter-scroll', 'login']
const showHeader = computed(() => {
  if (!route.name)
    return false
  return !hiddenRoutes.includes(route.name as string)
})

const showProgress = ref(false)
const progressComplete = ref(false)

// 监听路由变化，显示进度条
watch(() => route.path, () => {
  if (!showHeader.value)
    return

  showProgress.value = true
  progressComplete.value = false

  // 短暂延迟后让进度条填满
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      progressComplete.value = true
    })
  })

  // 动画完成后隐藏
  setTimeout(() => {
    showProgress.value = false
  }, 600)
})

function goHome() {
  router.push({ name: 'home' })
}

function handleRightAction() {
  if (isStorlyn) {
    router.push({ name: 'member' })
    return
  }

  emit('open-drawer')
}

const pageTitle = computed(() => {
  const routeTitle = route.meta?.title as string | undefined
  const queryTitle = route.query?.title as string | undefined

  if (route.name === 'ranking')
    return queryTitle || routeTitle || 'Ranking'

  if (route.name === 'collection')
    return queryTitle || routeTitle || 'Collection'

  return routeTitle || brandTexts.clientGroupName || 'Novel'
})

const showRightAction = computed(() => !(isStorlyn && route.name === 'member'))
</script>

<template>
  <div v-if="showHeader">
    <!-- Placeholder for layout spacing -->
    <div class="h-46px" />
    <!-- Fixed Header -->
    <div class="flipnovel-global-header">
      <!-- Progress Bar -->
      <div v-if="showProgress" class="progress-bar-container">
        <div
          class="progress-bar"
          :class="{ complete: progressComplete }"
          :style="{ background: primaryColor }"
        />
      </div>
      <!-- Top row: Home | Title | Menu -->
      <div class="flex items-center justify-between h-46px px-15px bg-n-bg-global">
        <div class="w-8 h-8 flex items-center justify-center -ml-1" @click="goHome">
          <svg class="w-6 h-6 block text-n-text-1" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M3.5 11.25L10.78 4.97C11.49 4.36 12.51 4.36 13.22 4.97L20.5 11.25C20.89 11.59 21.02 12.14 20.83 12.61C20.64 13.08 20.19 13.38 19.69 13.38H18.25V18C18.25 18.69 17.69 19.25 17 19.25H14.5V15.25C14.5 14.28 13.72 13.5 12.75 13.5H11.25C10.28 13.5 9.5 14.28 9.5 15.25V19.25H7C6.31 19.25 5.75 18.69 5.75 18V13.38H4.31C3.81 13.38 3.36 13.08 3.17 12.61C2.98 12.14 3.11 11.59 3.5 11.25Z"
              stroke="currentColor"
              stroke-width="1.9"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <div class="text-17px font-bold text-n-text-1 tracking-wide">
          {{ pageTitle }}
        </div>
        <div v-if="showRightAction" class="w-8 h-8 -mr-1 flex items-center justify-center" @click="handleRightAction">
          <svg v-if="isStorlyn" class="w-6 h-6 block text-n-text-1" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="8" r="4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M4 20c0-4 4-6 8-6s8 2 8 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <svg v-else class="w-5 h-5 block text-n-text-1" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M2.5 4.5H17.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            <path d="M2.5 10H17.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            <path d="M2.5 15.5H17.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </div>
        <div v-else class="w-8 h-8 -mr-1" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.flipnovel-global-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 20;
  max-width: 750px;
  margin: 0 auto;
}

/* Progress Bar */
.progress-bar-container {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: transparent;
  overflow: hidden;
  z-index: 21;
}

.progress-bar {
  height: 100%;
  width: 0;
  transition: width 0.5s ease-out;
}

.progress-bar.complete {
  width: 100%;
}

/* Drawer animations */
</style>
